import { getPinComponentBorderStyle, useUpdatePin } from '@lessonup/pin-renderer';
import { CategoryAreaPinComponent } from '@lessonup/pins-shared';
import { SpaceBetween, spacing, styled } from '@lessonup/ui-components';
import React from 'react';
import { useEditorTranslation } from '../../../../../hooks/useEditorTranslation';
import { setBorderStyleAction } from '../../../../../utils/pinComponentSettings/sharedPinComponentSettings';
import { SettingsToggle } from '../../../../SettingsToggle/SettingsToggle';

export interface BorderStyleSettingProps {
  pinComponent: CategoryAreaPinComponent;
}

const StyledBorderPreview = styled.div<{ $borderStyle: 'solid' | 'dashed' | 'dotted' }>`
  width: 20px;
  height: 2px;
  background-color: ${(props) => {
    if (props.$borderStyle === 'solid') {
      return 'currentColor';
    }
    return 'transparent';
  }};
  background-image: ${(props) => {
    if (props.$borderStyle === 'dashed') {
      return 'repeating-linear-gradient(to right, currentColor 0, currentColor 3px, transparent 3px, transparent 6px)';
    } else if (props.$borderStyle === 'dotted') {
      return 'repeating-linear-gradient(to right, currentColor 0, currentColor 2px, transparent 2px, transparent 4px)';
    }
    return 'none';
  }};
`;

const borderStyleOptions = [
  {
    value: 'solid' as const,
    labelKey: 'solid',
    preview: <StyledBorderPreview $borderStyle="solid" />,
  },
  {
    value: 'dashed' as const,
    labelKey: 'dashed',
    preview: <StyledBorderPreview $borderStyle="dashed" />,
  },
  {
    value: 'dotted' as const,
    labelKey: 'dotted',
    preview: <StyledBorderPreview $borderStyle="dotted" />,
  },
];

export const BorderStyleSetting: React.FC<BorderStyleSettingProps> = ({ pinComponent }) => {
  const dispatch = useUpdatePin();
  const { t } = useEditorTranslation();
  const currentStyle = getPinComponentBorderStyle(pinComponent);

  return (
    <SpaceBetween direction="x" spacing={spacing.size4} alignItems="center" justifyContent="space-around">
      {borderStyleOptions.map(({ value, labelKey, preview }) => (
        <SettingsToggle
          key={value}
          type="checkbox"
          toggleVariant="caption"
          caption={t(`contextSidebar.settings.borderStyle.${labelKey}`)}
          value={value}
          onChange={() => {
            dispatch(setBorderStyleAction(pinComponent.id, value));
          }}
          checked={currentStyle === value}
          icon={preview}
        />
      ))}
    </SpaceBetween>
  );
};
